import BackHeader from "@components/header/BackHeader";
import { SignupForm, EmailVerificationStep } from "./_components";
import styled from "@emotion/styled";
import SuccessMessage from "@components/success/SuccessMessage";
import useJoinStepStore from "@store/join/useJoinStepStore";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { joinSchema } from "@libs/zod/zodVaildation";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SIGN_UP_URL } from "@libs/apiUrl";
import { useState } from "react";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";

export interface IFormInput {
  password: string;
  passwordConfirm: string;
  name: string;
  nickname: string;
  country: string;
  address: string;
  address_detail: string;
  postcode: string;
  gender: string;
  year: string;
  month: string;
  day: string;
  profile_image: string | null;
  isNicknameUnique: boolean;
}

export default function JoinPage() {
  const { step, setStep, decreaseStep, resetStep } = useJoinStepStore();
  const lang = window.localStorage.getItem("language");
  const navigate = useNavigate();
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const methods = useForm<IFormInput>({
    resolver: zodResolver(joinSchema()),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      password: "",
      passwordConfirm: "",
      profile_image: null,
      name: "",
      nickname: "",
      country: "",
      postcode: "",
      address: "",
      address_detail: "",
      gender: "",
      year: "",
      month: "",
      day: "",
      isNicknameUnique: false,
    },
  });

  const { handleSubmit } = methods;

  const onSubmit = async (data: IFormInput) => {
    const signupEmail = sessionStorage.getItem("signup_email");
    if (!signupEmail) {
      setErrorMessage("이메일 인증이 필요합니다.");
      setErrorModalOpen(true);
      resetStep();
      return;
    }

    const postData = {
      language: lang,
      login_id: signupEmail,
      password: data.password,
      profile_image: null,
      name: data.name,
      nickname: data.nickname,
      gender: data.gender,
      country: data.country,
      postcode: data.postcode,
      address: data.address,
      address_detail: data.address_detail,
      birth_date: `${data.year}-${data.month}-${data.day}`,
    };

    try {
      const res = await axios.post(SIGN_UP_URL, postData);

      if (res.status === 200) {
        sessionStorage.removeItem("signup_email");
        setStep(2);
      }
    } catch (err: any) {
      const errorCode = err?.response?.data?.error_code;
      if (errorCode === "1008") {
        setErrorMessage("이메일 인증이 만료되었습니다. 다시 인증해주세요.");
        setErrorModalOpen(true);
        resetStep();
      } else {
        setErrorMessage(
          err?.response?.data?.message || "회원가입에 실패했습니다.",
        );
        setErrorModalOpen(true);
      }
    }
  };

  const handleBackEvent = () => {
    if (step === 0) {
      navigate(-1);
    } else {
      decreaseStep();
    }
  };

  const tabList = [
    // Step 0: 이메일 인증
    { component: <EmailVerificationStep /> },
    // Step 1: 프로필 폼
    {
      component: (
        <FormProvider {...methods}>
          <SignupForm onSubmit={handleSubmit(onSubmit)} />
        </FormProvider>
      ),
    },
    // Step 2: 완료 메시지
    {
      component: (
        <SuccessMessage
          message="회원가입이 완료되었습니다."
          buttonText="로그인 하기"
          onButtonClick={() => {
            navigate("/login");
          }}
        />
      ),
    },
  ];

  return (
    <>
      {step < 2 && <BackHeader title="회원가입" onClick={handleBackEvent} />}
      <Container>{tabList[step].component}</Container>

      <BasicModal
        open={errorModalOpen}
        setOpen={setErrorModalOpen}
        activeEvent={() => setErrorModalOpen(false)}
        activeName="확인"
        notice={true}
        padding="2rem"
      >
        <ModalContent mainMessage={errorMessage} />
      </BasicModal>
    </>
  );
}

const Container = styled.div(() => ({
  padding: "2rem",
}));
